import {
  buildRawSubSampleSchema,
  buildSubSamplePropertyList,
  Mtds
} from 'prove-common'
import { useMemo } from 'react'

type SubSampleSchemaSectionProps = {
  schemaJson: string
}

export const SubSampleSchemaSection = ({
  schemaJson
}: SubSampleSchemaSectionProps) => {
  const rows = useMemo(
    () => buildSubSamplePropertyList(schemaJson),
    [schemaJson]
  )

  const rawSubSampleJson = useMemo(
    () => buildRawSubSampleSchema(schemaJson),
    [schemaJson]
  )

  const downloadSchema = () => {
    const content = rawSubSampleJson ?? '{}'
    const blob = new Blob([content], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'sub-sample-schema.json'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  if (rows == null || rows.length === 0) return null

  return (
    <Mtds.Card as={Mtds.Grid} data-gap='6'>
      <Mtds.Grid data-gap='0'>
        <Mtds.Heading data-size='xs'>
          Sub-sample payload{' '}
          <Mtds.Tag data-color={'neutral'} data-icon={'none'}>
            schema
          </Mtds.Tag>
        </Mtds.Heading>
        <Mtds.Muted>
          Validates the <code>payload</code> field on each entry in{' '}
          <code>subSamples[]</code> — a JSON-encoded string with per-sub-sample
          data (e.g. animal ID, slaughter tag)
        </Mtds.Muted>
      </Mtds.Grid>
      <Mtds.Table data-size='sm'>
        <thead>
          <tr>
            <th>Property name</th>
            <th>Title</th>
            <th>Type</th>
            <th>Possible values</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.propertyKey}-${i}`}>
              <td>
                <code>{row.propertyKey}</code>
              </td>
              <td>{row.title ?? '-'}</td>
              <td>
                {row.type == null ? '-' : <Mtds.Muted>{row.type}</Mtds.Muted>}
              </td>
              <td>
                {row.enumValues != null && row.enumValues.length > 0 ? (
                  <span className='inline-flex flex-wrap gap-x-1'>
                    {row.enumValues.map((v, j) => (
                      <span key={v}>
                        <code>{v}</code>
                        {j < row.enumValues!.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </span>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Mtds.Table>
      {rawSubSampleJson != null && (
        <Mtds.Details data-variant='card'>
          <Mtds.Details.Summary>View raw JSON schema</Mtds.Details.Summary>
          <Mtds.Prose>
            <Mtds.Button
              data-variant='secondary'
              data-size='sm'
              onClick={downloadSchema}
            >
              Download JSON schema
            </Mtds.Button>
            <pre className='overflow-x-auto'>{rawSubSampleJson}</pre>
          </Mtds.Prose>
        </Mtds.Details>
      )}
    </Mtds.Card>
  )
}
